/**
 * Build a plain-text "playtest report" from a QA analysis result. Easy to
 * copy/paste into a playtest doc. Uses Markdown-style headings ("===" /
 * "---" underlines) so it still reads cleanly as raw text.
 *
 * `record` shape (matches the saved proposal payload):
 *   {
 *     name, tags, rule_type, created_at, snapshot_id, llm_model,
 *     proposed_text, alternative_text,
 *     result: {summary, semantic_similar, fast_similar, conflicts, ...},
 *     alternative_result, comparison
 *   }
 *
 * Returns a string ready to feed into a Blob download.
 */

const SEV_LABEL = { high: "HIGH", medium: "MEDIUM", low: "LOW" };

const ruleTypeLabel = (id) => {
  if (!id) return "—";
  return ({
    modelAbility: "Model Ability",
    cardAbility: "Card Ability",
    weaponAbility: "Weapon Quality",
    spell: "Spell",
    animus: "Animus",
    feat: "Feat / Special Action",
    other: "Other",
  })[id] || id;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + " UTC"; }
  catch { return iso; }
};

const h1 = (s) => `${s}\n${"=".repeat(Math.max(3, s.length))}\n`;
const h2 = (s) => `\n${s}\n${"-".repeat(Math.max(3, s.length))}\n`;
const h3 = (s) => `\n### ${s}\n`;

function indent(text, prefix = "    ") {
  return (text || "")
    .toString()
    .split("\n")
    .map((l) => (l ? prefix + l : l))
    .join("\n");
}

function renderProposalBlock(label, text) {
  if (!text) return "";
  return `${h2(label)}\n${text.trim()}\n`;
}

function renderSummary(result) {
  if (!result?.summary) return "";
  return h2("QA Summary") + "\n" + result.summary.trim() + "\n";
}

function renderConflicts(result) {
  const conflicts = result?.conflicts || [];
  if (conflicts.length === 0) {
    return h2("Rule Conflicts") + "\nNo conflicts detected.\n";
  }
  // Counts
  const sev = { high: 0, medium: 0, low: 0 };
  for (const c of conflicts) sev[c.severity in sev ? c.severity : "low"] += 1;
  const rb = conflicts.filter((c) => (c.source || "rulebook") === "rulebook");
  const sr = conflicts.filter((c) => c.source === "steamroller");

  let out = h2(`Rule Conflicts (${conflicts.length})`);
  out += `\nSummary: ${sev.high} HIGH · ${sev.medium} MEDIUM · ${sev.low} LOW`;
  out += `   |   ${rb.length} Main Rulebook · ${sr.length} Steamroller\n`;

  const sevOrder = { high: 0, medium: 1, low: 2 };
  const renderGroup = (title, list) => {
    if (list.length === 0) return "";
    list = [...list].sort((a, b) =>
      (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9) ||
      (a.section_title || "").localeCompare(b.section_title || "")
    );
    let block = h3(`${title} — ${list.length}`);
    for (const c of list) {
      block += `\n* [${SEV_LABEL[c.severity] || "?"}] ${c.section_title || "(no title)"}\n`;
      if (c.explanation) block += `\n${indent(c.explanation)}\n`;
      if (c.relevant_excerpt) block += `\n${indent('"' + c.relevant_excerpt + '"', "      ")}\n`;
    }
    return block;
  };
  out += renderGroup("Main Rulebook", rb);
  out += renderGroup("Steamroller (Organized Play)", sr);
  return out;
}

function renderSemanticMatches(result) {
  const list = result?.semantic_similar || [];
  if (list.length === 0) return h2("Semantic Matches") + "\nNone.\n";
  let out = h2(`Semantic Matches (${list.length})`);
  for (const m of list) {
    out += `\n* ${m.name || "(no name)"} — ${m.section || "?"} · score ${(m.score ?? 0).toFixed(2)}\n`;
    if (m.explanation) out += `\n${indent(m.explanation)}\n`;
    if (m.text) out += `\n${indent(m.text, "      ")}\n`;
  }
  return out;
}

function renderTextualMatches(result) {
  const list = result?.fast_similar || [];
  if (list.length === 0) return h2("Textual Matches") + "\nNone.\n";
  let out = h2(`Textual Matches (${list.length})`);
  for (const m of list) {
    out += `\n* ${m.name || "(no name)"} — ${m.section || "?"} · ${(m.score ?? m.similarity ?? 0).toFixed(2)}`;
    if (m.top_tokens && m.top_tokens.length)
      out += `   (shared: ${m.top_tokens.slice(0, 6).join(", ")})`;
    out += "\n";
    if (m.text) out += `\n${indent(m.text, "      ")}\n`;
  }
  return out;
}

function renderRevisionSuggestion(result) {
  const rev = result?.revision_suggestion;
  if (!rev || !rev.revised_text) return "";
  let out = h2("Suggested Revised Wording (LLM)");
  out += "\n" + rev.revised_text.trim() + "\n";
  if (rev.change_summary) {
    out += h3("What changed");
    out += "\n" + rev.change_summary.trim() + "\n";
  }
  const resolutions = rev.resolutions || [];
  if (resolutions.length > 0) {
    out += h3(`Conflict resolution breakdown (${resolutions.length})`);
    const counts = { addressed: 0, "not-addressable": 0, "accepted-tension": 0 };
    for (const r of resolutions) if (r.status in counts) counts[r.status] += 1;
    out += `\n${counts.addressed} addressed · ${counts["not-addressable"]} cannot fix · ${counts["accepted-tension"]} accepted tension\n`;
    for (const r of resolutions) {
      const tag = ({
        "addressed": "ADDRESSED",
        "not-addressable": "CANNOT FIX",
        "accepted-tension": "TENSION OK",
      })[r.status] || (r.status || "?").toUpperCase();
      const src = r.source === "steamroller" ? "Steamroller" : "Main Rulebook";
      out += `\n* [${tag}] ${r.section_title || "?"}  (${src}/${r.severity || "?"})`;
      if (r.note) out += `\n${indent(r.note)}`;
      out += "\n";
    }
  }
  return out;
}

export function buildPlaytestReport(record) {
  const created = record.created_at || new Date().toISOString();
  const result = record.result || record; // ProposePage passes the live `result` directly
  const alt = record.alternative_result || null;
  const cmp = record.comparison || null;

  // Header
  let out = h1(`Morrstofa — Playtest Report`);
  out += `Proposal:   ${record.name || "(untitled)"}\n`;
  out += `Generated:  ${fmtDate(created)}\n`;
  out += `Rule type:  ${ruleTypeLabel(record.rule_type || result?.rule_type)}\n`;
  if (record.tags && record.tags.length) out += `Tags:       ${record.tags.join(", ")}\n`;
  if (record.snapshot_id) out += `Snapshot:   ${record.snapshot_id}\n`;
  if (record.llm_model) out += `LLM model:  ${record.llm_model}\n`;

  // Proposal wording (and optional B side)
  out += renderProposalBlock("Proposed Wording", record.proposed_text);
  if (record.alternative_text) {
    out += renderProposalBlock("Alternative Wording (Side B)", record.alternative_text);
  }

  // QA summary
  out += renderSummary(result);

  // Compare summary
  if (cmp?.summary) {
    out += h2("A vs B Comparison") + "\n" + cmp.summary.trim() + "\n";
    if (cmp.overlap_ratio != null)
      out += `\nKeyword overlap: ${(cmp.overlap_ratio * 100).toFixed(0)}%\n`;
  }

  // Conflicts, semantic, textual — side A
  out += renderConflicts(result);
  out += renderRevisionSuggestion(result);
  out += renderSemanticMatches(result);
  out += renderTextualMatches(result);

  // Side B blocks if present
  if (alt) {
    out += "\n\n" + h1("Side B");
    out += renderSummary(alt);
    out += renderConflicts(alt);
    out += renderSemanticMatches(alt);
    out += renderTextualMatches(alt);
  }

  out += `\n\n---\nEnd of report.\n`;
  return out;
}

/** Trigger a browser download for the given text content. */
export function downloadText(filename, body) {
  const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Slugify a string for safe use in a filename. */
export function slugify(s) {
  return (s || "proposal")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "proposal";
}
